// Product Service for managing product operations
const API_BASE_URL = '/api/v1';

class ProductService {
  // Cache for products data
  static cache = {
    products: [],
    categories: [],
    pagination: null,
    lastFetch: null,
    lastCategoryFetch: null,
    searchParams: null,
    version: 0
  };
  // Get all products with pagination and filters (with caching)
  static async getAllProducts(params = {}) {
    try {
      const cacheKey = JSON.stringify(params);
      const now = Date.now();
      const cacheExpiry = 5 * 60 * 1000; // 5 minutes cache expiry

      // Check if we should bypass cache
      const bypassCache = params.bypassCache === true;
      
      // Remove bypassCache from params before creating cache key and making request
      const { bypassCache: _, ...requestParams } = params;
      const requestCacheKey = JSON.stringify(requestParams);

      // Check if we have valid cached data for the same parameters (unless bypassing cache)
      if (
        !bypassCache &&
        this.cache.lastFetch &&
        (now - this.cache.lastFetch < cacheExpiry) &&
        this.cache.searchParams === requestCacheKey &&
        this.cache.products.length > 0
      ) {
        console.log('Returning cached products data');
        return {
          products: this.cache.products,
          pagination: this.cache.pagination,
          cached: true
        };
      }

      const queryParams = new URLSearchParams();

      if (requestParams.page) queryParams.append('page', requestParams.page);
      if (requestParams.limit) queryParams.append('limit', requestParams.limit);
      if (requestParams.search) queryParams.append('search', requestParams.search);
      if (requestParams.category) queryParams.append('category', requestParams.category);
      if (requestParams.status) queryParams.append('status', requestParams.status);

      const url = `${API_BASE_URL}/products${queryParams.toString() ? '?' + queryParams.toString() : ''}`;

      const response = await fetch(url, {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        }
      });

      const data = await response.json();

      // Handle auth/permission errors
      if (response.status === 401) {
        // Only 401 (invalid/expired token) should force logout
        localStorage.removeItem('token');
        localStorage.removeItem('userEmail');
        localStorage.removeItem('userPermissions');
        window.location.href = '/login';
        throw new Error('Session expired. Please login again.');
      }

      if (response.status === 403) {
        // 403 = forbidden (e.g. missing permission). Do NOT logout; let UI show Access Denied.
        const message = data?.message || 'You do not have permission to view products.';
        throw new Error(message);
      }

      if (!response.ok) {
        throw new Error(data?.message || `Failed to fetch products: ${response.status}`);
      }

      // Update cache
      this.cache.products = data.data || [];
      this.cache.pagination = data.pagination;
      this.cache.lastFetch = now;
      this.cache.searchParams = requestCacheKey;
      this.cache.version++;

      console.log('Fetched fresh products data from API', data);
      return {
        products: data.data || [],
        pagination: data.pagination,
        cached: false
      };
    } catch (error) {
      console.error('Get products error:', error);

      // IMPORTANT:
      // Do NOT force logout here on 403. Only 401 should have triggered logout above.
      // Here we just bubble the error so the UI can show an appropriate message.
      if (error instanceof Error) {
        throw error;
      }
      throw new Error('Failed to fetch products');
    }
  }

  // Get product categories (with caching)
  static async getProductCategories() {
    try {
      const now = Date.now();
      const cacheExpiry = 10 * 60 * 1000; // 10 minutes for categories (less frequent changes)

      // Check if we have valid cached categories
      if (
        this.cache.lastCategoryFetch &&
        (now - this.cache.lastCategoryFetch < cacheExpiry) &&
        this.cache.categories.length > 0
      ) {
        console.log('Returning cached categories data');
        return {
          categories: this.cache.categories,
          cached: true
        };
      }

      const response = await fetch(`${API_BASE_URL}/products/categories`, {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        }
      });

      const data = await response.json();

      if (response.status === 401 || response.status === 403) {
        // Token expired or invalid - logout user
        localStorage.removeItem('token');
        localStorage.removeItem('userEmail');
        localStorage.removeItem('userPermissions');
        window.location.href = '/login';
        throw new Error('Session expired. Please login again.');
      }

      if (!response.ok) {
        throw new Error(data?.message || `Failed to fetch categories: ${response.status}`);
      }

      // Update cache
      this.cache.categories = data.categories;
      this.cache.lastCategoryFetch = now;

      console.log('Fetched fresh categories data from API');
      return data;
    } catch (error) {
      console.error('Get categories error:', error);
      throw new Error(error?.message || 'Failed to fetch categories');
    }
  }

  // Get product by ID
  static async getProductById(id) {
    try {
      const response = await fetch(`${API_BASE_URL}/products/${id}`, {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        }
      });

      const data = await response.json();

      if (!response.ok) {
        throw new Error(data?.message || `Failed to fetch product: ${response.status}`);
      }

      return data;
    } catch (error) {
      console.error('Get product error:', error);
      throw new Error(error?.message || 'Failed to fetch product');
    }
  }

  // Create new product (invalidates cache)
  static async createProduct(productData, files = {}, onProgress = null) {
    try {
      console.log('Frontend: createProduct called with:', { productData, files });

      const hasFiles = Object.keys(files).length > 0;
      console.log('Frontend: Has files:', hasFiles);

      let response;

      if (hasFiles) {
        // Use XMLHttpRequest for file uploads with progress tracking
        console.log('Frontend: Using XMLHttpRequest for file upload with progress');

        const result = await new Promise((resolve, reject) => {
          const xhr = new XMLHttpRequest();

          // Initialize progress tracking variables
          let uploadStartTime = Date.now();
          let lastLoaded = 0;
          let lastTime = uploadStartTime;

          // Set up progress tracking
          if (onProgress) {
            xhr.upload.addEventListener('progress', (e) => {
              if (e.lengthComputable) {
                const percentComplete = (e.loaded / e.total) * 100;
                const currentTime = Date.now();
                const elapsedTime = (currentTime - uploadStartTime) / 1000; // seconds
                const bytesSinceLast = e.loaded - lastLoaded;
                const timeSinceLast = (currentTime - lastTime) / 1000;

                // Calculate upload speed (bytes per second)
                const speed = bytesSinceLast > 0 && timeSinceLast > 0 ? bytesSinceLast / timeSinceLast : 0;

                // Calculate estimated time remaining
                const remainingBytes = Math.max(0, e.total - e.loaded);
                const timeRemaining = speed > 0 ? remainingBytes / speed : (percentComplete < 100 ? Number.POSITIVE_INFINITY : 0);

                // Update tracking variables
                lastLoaded = e.loaded;
                lastTime = currentTime;

                onProgress({
                  percentage: percentComplete,
                  loaded: e.loaded,
                  total: e.total,
                  speed: speed,
                  timeRemaining: timeRemaining,
                  elapsedTime: elapsedTime
                });
              }
            });
          }

          xhr.addEventListener('load', () => {
            if (xhr.status >= 200 && xhr.status < 300) {
              try {
                const data = JSON.parse(xhr.responseText);
                resolve({ response: { ok: true, status: xhr.status }, data });
              } catch (e) {
                resolve({ response: { ok: true, status: xhr.status }, data: {} });
              }
            } else {
              try {
                const errorData = JSON.parse(xhr.responseText);
                reject(new Error(errorData?.message || `HTTP ${xhr.status}`));
              } catch (e) {
                reject(new Error(`HTTP ${xhr.status}`));
              }
            }
          });

          xhr.addEventListener('error', () => {
            reject(new Error('Network error'));
          });

          xhr.open('POST', `${API_BASE_URL}/products`);
          xhr.setRequestHeader('Authorization', `Bearer ${localStorage.getItem('token')}`);

          const formData = new FormData();

          // Add all product data to FormData - only add non-empty values
          Object.keys(productData).forEach(key => {
            const value = productData[key];
            if (value !== null && value !== undefined && value !== '') {
              formData.append(key, String(value));
              console.log(`Frontend: Added ${key}:`, value);
            }
          });

          // Add files to FormData if provided
          Object.keys(files).forEach(fileKey => {
            if (files[fileKey]) {
              console.log(`Frontend: Adding file ${fileKey}:`, files[fileKey]);
              formData.append(fileKey, files[fileKey]);
            }
          });

          console.log('Frontend: FormData created, sending request...');
          xhr.send(formData);
        });

        response = result.response;
        var data = result.data;
      } else {
        // Use JSON for regular creation
        console.log('Frontend: Using JSON for regular creation');

        response = await fetch(`${API_BASE_URL}/products`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${localStorage.getItem('token')}`
          },
          body: JSON.stringify(productData)
        });

        const textResponse = await response.text();
        if (textResponse.trim() === '') {
          data = {};
        } else {
          data = JSON.parse(textResponse);
        }
      }

      // Invalidate cache since data changed
      this.invalidateCache();
      console.log('Frontend: Cache invalidated after product creation');

      return data;
    } catch (error) {
      console.error('Frontend: Create product error:', error);
      throw new Error(error?.message || 'Failed to create product');
    }
  }

  // Update product (invalidates cache)
  static async updateProduct(id, productData, files = {}, onProgress = null) {
    try {
      console.log('Frontend: updateProduct called with:', { id, productData, files });
      console.log('Frontend: Files object:', files);

      const hasFiles = Object.keys(files).length > 0;
      console.log('Frontend: Has files:', hasFiles);

      let response;

      if (hasFiles) {
        // Use FormData for file uploads
        console.log('Frontend: Using FormData for file upload');

        const formData = new FormData();

        // Add all product data to FormData - only add non-empty values
        Object.keys(productData).forEach(key => {
          const value = productData[key];
          if (value !== null && value !== undefined && value !== '') {
            formData.append(key, String(value));
            console.log(`Frontend: Added ${key}:`, value);
          }
        });

        // Add files to FormData if provided
        Object.keys(files).forEach(fileKey => {
          if (files[fileKey]) {
            console.log(`Frontend: Adding file ${fileKey}:`, files[fileKey]);
            formData.append(fileKey, files[fileKey]);
          }
        });

        console.log('Frontend: FormData created, sending request...');

        // Use XMLHttpRequest for file uploads with progress tracking
        console.log('Frontend: Using XMLHttpRequest for update file upload with progress');

        const result = await new Promise((resolve, reject) => {
          const xhr = new XMLHttpRequest();

          // Set up progress tracking with time estimation
          let uploadStartTime = Date.now();
          let lastLoaded = 0;
          let lastTime = uploadStartTime;

          if (onProgress) {
            xhr.upload.addEventListener('progress', (e) => {
              if (e.lengthComputable) {
                const percentComplete = (e.loaded / e.total) * 100;
                const currentTime = Date.now();
                const elapsedTime = (currentTime - uploadStartTime) / 1000; // seconds
                const bytesSinceLast = e.loaded - lastLoaded;
                const timeSinceLast = (currentTime - lastTime) / 1000;

                // Calculate upload speed (bytes per second)
                const speed = timeSinceLast > 0 ? bytesSinceLast / timeSinceLast : 0;

                // Calculate estimated time remaining
                const remainingBytes = e.total - e.loaded;
                const timeRemaining = speed > 0 ? remainingBytes / speed : 0;

                // Update tracking variables
                lastLoaded = e.loaded;
                lastTime = currentTime;

                onProgress({
                  percentage: percentComplete,
                  loaded: e.loaded,
                  total: e.total,
                  speed: speed,
                  timeRemaining: timeRemaining,
                  elapsedTime: elapsedTime
                });
              }
            });
          }

          xhr.addEventListener('load', () => {
            if (xhr.status >= 200 && xhr.status < 300) {
              try {
                const data = JSON.parse(xhr.responseText);
                resolve({ response: { ok: true, status: xhr.status }, data });
              } catch (e) {
                resolve({ response: { ok: true, status: xhr.status }, data: {} });
              }
            } else {
              try {
                const errorData = JSON.parse(xhr.responseText);
                reject(new Error(errorData?.message || `HTTP ${xhr.status}`));
              } catch (e) {
                reject(new Error(`HTTP ${xhr.status}`));
              }
            }
          });

          xhr.addEventListener('error', () => {
            reject(new Error('Network error'));
          });

          xhr.open('PUT', `${API_BASE_URL}/products/${id}`);
          xhr.setRequestHeader('Authorization', `Bearer ${localStorage.getItem('token')}`);
          xhr.send(formData);
        });

        response = result.response;
        var data = result.data;
      } else {
        // Use JSON for regular updates
        console.log('Frontend: Using JSON for regular update');

        response = await fetch(`${API_BASE_URL}/products/${id}`, {
          method: 'PUT',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${localStorage.getItem('token')}`
          },
          body: JSON.stringify(productData)
        });

        const textResponse = await response.text();
        console.log('Frontend: Raw response text:', textResponse);

        if (textResponse.trim() === '') {
          console.log('Frontend: Empty response');
          data = {};
        } else {
          data = JSON.parse(textResponse);
          console.log('Frontend: Parsed response data:', data);
        }
      }

      // Check response
      if (response.status === 401 || response.status === 403) {
        // Token expired or invalid - logout user
        localStorage.removeItem('token');
        localStorage.removeItem('userEmail');
        localStorage.removeItem('userPermissions');
        window.location.href = '/login';
        throw new Error('Session expired. Please login again.');
      }

      if (!response.ok) {
        console.error('Frontend: Request failed with status:', response.status);
        throw new Error(data?.message || `Failed to update product: ${response.status}`);
      }

      // Invalidate cache since data changed
      this.invalidateCache();
      console.log('Frontend: Cache invalidated after product update');

      console.log('Frontend: Update successful, returning data');
      return data;
    } catch (error) {
      console.error('Frontend: Update product error:', error);
      throw new Error(error?.message || 'Failed to update product');
    }
  }

  // Delete product (invalidates cache)
  static async deleteProduct(id) {
    try {
      const response = await fetch(`${API_BASE_URL}/products/${id}`, {
        method: 'DELETE',
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        }
      });

      if (!response.ok) {
        const data = await response.json().catch(() => ({}));
        throw new Error(data?.message || `Failed to delete product: ${response.status}`);
      }

      // Invalidate cache since data changed
      this.invalidateCache();
      console.log('Cache invalidated after product deletion');

      return { success: true };
    } catch (error) {
      console.error('Delete product error:', error);
      throw new Error(error?.message || 'Failed to delete product');
    }
  }

  // Bulk delete products (invalidates cache)
  static async bulkDeleteProducts(ids) {
    try {
      const deletePromises = ids.map(id => this.deleteProduct(id));
      const results = await Promise.allSettled(deletePromises);

      const successful = results.filter(result => result.status === 'fulfilled').length;
      const failed = results.filter(result => result.status === 'rejected').length;

      // Invalidate cache since data changed
      if (successful > 0) {
        this.invalidateCache();
        console.log('Cache invalidated after bulk product deletion');
      }

      return {
        success: true,
        successful,
        failed,
        total: ids.length
      };
    } catch (error) {
      console.error('Bulk delete error:', error);
      throw new Error(error?.message || 'Failed to delete products');
    }
  }

  // Invalidate cache method
  static invalidateCache() {
    this.cache.lastFetch = null;
    this.cache.searchParams = null;
    this.cache.version++;
    console.log('Product cache invalidated, version:', this.cache.version);
  }

  // Clear all cache data
  static clearCache() {
    this.cache = {
      products: [],
      categories: [],
      pagination: null,
      lastFetch: null,
      lastCategoryFetch: null,
      searchParams: null,
      version: 0
    };
    console.log('Product cache cleared completely');
  }
}

export default ProductService;
